/**
 * Firestore write helpers for the task organiser. Same pattern as the
 * guests / seating modules — explicit weddingId, no implicit context,
 * easy to unit-test if we ever want to.
 */

import { addDoc, collection, deleteDoc, doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { Task, TaskStatus } from "@amazing-wedding/shared-types";

export type TaskDraft = Omit<Task, "id" | "createdAt" | "updatedAt" | "completedAt">;
export type TaskPatch = Partial<Omit<Task, "id" | "createdAt" | "updatedAt">>;

export async function createTask(weddingId: string, draft: TaskDraft) {
  const col = collection(db, "weddings", weddingId, "tasks");
  const ref = await addDoc(col, {
    ...draft,
    completedAt: draft.status === "done" ? serverTimestamp() : null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateTask(weddingId: string, taskId: string, patch: TaskPatch) {
  const ref = doc(db, "weddings", weddingId, "tasks", taskId);
  await updateDoc(ref, {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteTask(weddingId: string, taskId: string) {
  await deleteDoc(doc(db, "weddings", weddingId, "tasks", taskId));
}

/**
 * v0.4-α status flow without drag-and-drop: clicking the status pill on
 * a TaskCard cycles to the next state. `done` writes `completedAt` for
 * future "did N tasks this week" reporting; cycling back clears it.
 */
const NEXT_STATUS: Record<TaskStatus, TaskStatus> = {
  todo: "inProgress",
  inProgress: "done",
  done: "todo",
};

export async function cycleStatus(weddingId: string, taskId: string, current: TaskStatus) {
  const next = NEXT_STATUS[current];
  await updateTask(weddingId, taskId, {
    status: next,
    completedAt: next === "done" ? new Date().toISOString() : null,
  });
}
